import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api, FalhaDaApi } from '../api/cliente';
import {
  Alerta,
  Botao,
  CabecalhoDaPagina,
  Carregando,
  Cartao,
  Indicador,
  TituloDeSecao,
  Vazio,
} from '../componentes/base';
import { Seletor } from '../componentes/campos';
import { Funil, type FaseDoFunil } from '../componentes/Funil';
import {
  data,
  dataHora,
  documento,
  mesCurto,
  mesPorExtenso,
  moeda,
  percentual,
  plural,
} from '../util/formato';

interface RelatorioMensal {
  readonly emitidoEm: string;
  readonly mes: string;
  readonly nomeDoMes: string;
  readonly cabecalho: {
    clinicaId: string;
    nome: string;
    cnpj: string | null;
    percentualHonorarios: number;
    mensalidade: number;
    diaRepasse: number;
    adiantaCustas: boolean;
    responsavel: string | null;
    contratoAssinadoEm: string | null;
    contratoMeses: number | null;
  };
  readonly resumo: {
    recebido: number;
    quantidadePagamentos: number;
    honorarios: number;
    ficouComClinica: number;
    confere: boolean;
    previsto: number;
    quantidadePrevista: number;
    percentualRealizado: number | null;
    emAtraso: number;
    quantidadeEmAtraso: number;
    carteira: number;
    quantidadeDevedores: number;
    recuperadoDesdeInicio: number;
    taxaRecuperacao: number;
  };
  readonly pagamentos: {
    linhas: Array<{
      pagamentoId: string;
      devedorId: string;
      devedor: string;
      parcelaNumero: number;
      vencimento: string;
      dataPagamento: string;
      forma: string;
      valorParcela: number;
      valorPago: number;
      acrescimo: number;
      honorarios: number;
      liquidoClinica: number;
      percentualAplicado: number;
    }>;
    totais: {
      valorPago: number;
      honorarios: number;
      liquidoClinica: number;
      acrescimo: number;
    };
    porForma: Array<{ forma: string; quantidade: number; total: number }>;
  };
  readonly fechamento: {
    baseCalculo: number;
    percentualVigente: number;
    honorariosExito: number;
    alertaPercentualZeradoComHonorario: boolean;
    mensalidade: number;
    reembolsoCustas: number;
    custas: Array<{ devedorId: string; nome: string; valor: number }>;
    totalDevidoAoEscritorio: number;
    diaRepasse: number;
  };
  readonly funil: {
    aoFimDoMes: FaseDoFunil[];
    fasesVazias: string[];
    variacaoDisponivel: boolean;
  };
  readonly movimentacao: {
    novos: Array<{
      id: string;
      nome: string;
      valorOriginal: number;
      faseInicial: string;
      cadastradoEm: string;
    }>;
    quitados: Array<{
      id: string;
      nome: string;
      valorOriginal: number;
      recuperado: number;
      em: string;
      descricao: string;
    }>;
    encaminhadosJudicial: Array<{
      id: string;
      nome: string;
      valorOriginal: number;
      faseAtual: string;
      em: string;
      descricao: string;
    }>;
    acordosFirmados: Array<{
      id: string;
      devedorId: string;
      nome: string;
      tipo: string;
      valorTotal: number;
      quantidadeParcelas: number;
      dataPrimeiraParcela: string;
      firmadoEm: string;
    }>;
  };
  readonly negociacao: Array<{
    id: string;
    nome: string;
    valorOriginal: number;
    diasNaFase: number;
    ultimaAtualizacao: { em: string; por: string; trecho: string } | null;
  }>;
  readonly atualizacoes: Array<{
    id: string;
    em: string;
    por: string;
    devedorId: string;
    nome: string;
    texto: string;
  }>;
  readonly promessas: { disponivel: boolean; motivo: string; linhas: readonly unknown[] };
}

const FORMA: Record<string, string> = {
  PIX: 'Pix',
  BOLETO: 'Boleto',
  TRANSFERENCIA: 'Transferência',
  DINHEIRO: 'Dinheiro',
  CARTAO: 'Cartão',
  OUTRO: 'Outro',
};

export function PaginaRelatorios() {
  const [params, setParams] = useSearchParams();
  const clinicaId = params.get('clinicaId') ?? '';
  const mesPadrao = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  })();
  const mes = params.get('mes') ?? mesPadrao;

  const meses = Array.from({ length: 12 }, (_, i) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - i);
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    return { valor: iso, rotulo: i === 0 ? `${mesCurto(iso)} (atual)` : mesPorExtenso(iso) };
  });

  const [baixandoPdf, setBaixandoPdf] = useState(false);
  const [erroPdf, setErroPdf] = useState<string | null>(null);

  const { data: clinicas } = useQuery({
    queryKey: ['clinicas', 'selecao'],
    queryFn: () => api.buscar<Array<{ id: string; nome: string }>>('/clinicas/selecao'),
  });

  const { data: relatorio, isLoading, isFetching } = useQuery({
    queryKey: ['relatorio-mensal', clinicaId, mes],
    queryFn: () =>
      api.buscar<RelatorioMensal>('/relatorios/mensal', { clinicaId, mes }),
    enabled: Boolean(clinicaId && mes),
  });

  function definir(chave: 'clinicaId' | 'mes', valor: string) {
    const novo = new URLSearchParams(params);
    if (valor) novo.set(chave, valor);
    else novo.delete(chave);
    setParams(novo, { replace: true });
  }

  async function baixarPdf() {
    if (!clinicaId || !mes) return;
    setBaixandoPdf(true);
    setErroPdf(null);
    try {
      const url = new URL('/api/relatorios/mensal/pdf', window.location.origin);
      url.searchParams.set('clinicaId', clinicaId);
      url.searchParams.set('mes', mes);
      const r = await fetch(url, { credentials: 'include' });
      if (!r.ok) {
        let mensagem = 'Não foi possível gerar o PDF.';
        try {
          const corpo = (await r.json()) as { mensagem?: string };
          if (corpo.mensagem) mensagem = corpo.mensagem;
        } catch {
          /* corpo não é JSON */
        }
        throw new Error(mensagem);
      }
      const blob = await r.blob();
      const nome =
        r.headers.get('Content-Disposition')?.match(/filename="([^"]+)"/)?.[1] ??
        `relatorio-${mes}.pdf`;
      const objeto = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objeto;
      a.download = nome;
      a.click();
      URL.revokeObjectURL(objeto);
    } catch (e) {
      setErroPdf(
        e instanceof FalhaDaApi
          ? e.message
          : e instanceof Error
            ? e.message
            : 'Não foi possível gerar o PDF.',
      );
    } finally {
      setBaixandoPdf(false);
    }
  }

  return (
    <>
      <CabecalhoDaPagina
        titulo="Relatório mensal"
        descricao="Uma clínica por competência — o que a Bianca envia e o que o escritório confere"
        acoes={
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-64">
              <Seletor
                rotulo="Clínica"
                valor={clinicaId}
                aoMudar={(v) => definir('clinicaId', v)}
                vazio="Escolha a clínica"
                opcoes={(clinicas ?? []).map((c) => ({ valor: c.id, rotulo: c.nome }))}
              />
            </div>
            <div className="w-48">
              <Seletor
                rotulo="Mês"
                valor={mes}
                aoMudar={(v) => definir('mes', v)}
                opcoes={meses.map((m) => ({ valor: m.valor, rotulo: m.rotulo }))}
              />
            </div>
            <Botao
              tipo="primario"
              onClick={() => void baixarPdf()}
              desabilitado={!clinicaId || !relatorio || baixandoPdf}
            >
              {baixandoPdf ? 'Gerando…' : 'Baixar em PDF'}
            </Botao>
          </div>
        }
      />

      {erroPdf && (
        <div className="mb-4">
          <Alerta tom="vencido">{erroPdf}</Alerta>
        </div>
      )}

      {!clinicaId && (
        <Vazio titulo="Escolha a clínica para montar o relatório do mês." />
      )}

      {clinicaId && (isLoading || isFetching) && !relatorio && <Carregando />}

      {relatorio && (
        <div className="flex flex-col gap-6">
          {/* 1. Cabeçalho */}
          <Cartao>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-[color:var(--texto-tenue)]">
                  Relatório de {relatorio.nomeDoMes.toLowerCase()} · emitido em{' '}
                  {data(relatorio.emitidoEm)}
                </p>
                <h2 className="mt-1 text-xl text-[color:var(--texto)]">
                  {relatorio.cabecalho.nome}
                </h2>
                <p className="mt-1 text-sm text-[color:var(--texto-suave)]">
                  CNPJ {documento(relatorio.cabecalho.cnpj)}
                  {relatorio.cabecalho.responsavel
                    ? ` · ${relatorio.cabecalho.responsavel}`
                    : ''}
                </p>
              </div>
              <div className="text-right text-sm text-[color:var(--texto-suave)]">
                <p>Êxito vigente: {percentual(relatorio.cabecalho.percentualHonorarios)}</p>
                <p>Mensalidade: {moeda(relatorio.cabecalho.mensalidade)}</p>
                <p>Repasse até o dia {relatorio.cabecalho.diaRepasse}</p>
              </div>
            </div>
          </Cartao>

          {/* 2. Resumo */}
          <section>
            <TituloDeSecao>Resumo do mês</TituloDeSecao>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Indicador
                rotulo="Recebido no mês"
                valor={moeda(relatorio.resumo.recebido)}
                nota={plural(
                  relatorio.resumo.quantidadePagamentos,
                  'pagamento',
                  'pagamentos',
                )}
                tom="recebido"
              />
              <Indicador
                rotulo="Honorários do escritório"
                valor={moeda(relatorio.resumo.honorarios)}
                nota="valor congelado em cada baixa"
                tom="acento"
              />
              <Indicador
                rotulo="Ficou com a clínica"
                valor={moeda(relatorio.resumo.ficouComClinica)}
                nota={
                  relatorio.resumo.confere
                    ? 'fecha com recebido − honorários'
                    : 'não fecha — conferir baixas'
                }
                tom={relatorio.resumo.confere ? 'neutro' : 'vencido'}
              />
              <Indicador
                rotulo="Previsto × realizado"
                valor={
                  relatorio.resumo.percentualRealizado != null
                    ? percentual(relatorio.resumo.percentualRealizado)
                    : '—'
                }
                nota={`previsto ${moeda(relatorio.resumo.previsto)} · realizado ${moeda(relatorio.resumo.recebido)}`}
              />
              <Indicador
                rotulo="Carteira e recuperação"
                valor={percentual(relatorio.resumo.taxaRecuperacao)}
                nota={`${moeda(relatorio.resumo.carteira)} · ${plural(
                  relatorio.resumo.quantidadeDevedores,
                  'devedor',
                  'devedores',
                )}`}
              />
            </div>
            {!relatorio.resumo.confere && (
              <p className="mt-3 text-xs" style={{ color: 'var(--color-vencido)' }}>
                Recebido ≠ honorários + líquido da clínica — há baixa para conferir.
              </p>
            )}
          </section>

          {/* 3. Pagamentos */}
          <Cartao semPadding>
            <div className="p-5 pb-3">
              <TituloDeSecao
                auxiliar={
                  relatorio.pagamentos.porForma.length > 0
                    ? relatorio.pagamentos.porForma
                        .map((f) => `${FORMA[f.forma] ?? f.forma}: ${moeda(f.total)}`)
                        .join(' · ')
                    : undefined
                }
              >
                Pagamentos do mês
              </TituloDeSecao>
            </div>
            {relatorio.pagamentos.linhas.length === 0 ? (
              <p className="px-5 pb-5 text-sm text-[color:var(--texto-tenue)]">
                Nenhum pagamento com data neste mês.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-y text-xs text-[color:var(--texto-tenue)]">
                      <th className="px-5 py-2 font-medium">Devedor</th>
                      <th className="px-3 py-2 font-medium">Parc.</th>
                      <th className="px-3 py-2 font-medium">Vencimento</th>
                      <th className="px-3 py-2 font-medium">Pago em</th>
                      <th className="px-3 py-2 font-medium">Forma</th>
                      <th className="px-3 py-2 text-right font-medium">Valor</th>
                      <th className="px-3 py-2 text-right font-medium">Acrésc.</th>
                      <th className="px-3 py-2 text-right font-medium">Êxito</th>
                      <th className="px-5 py-2 text-right font-medium">Líquido</th>
                    </tr>
                  </thead>
                  <tbody>
                    {relatorio.pagamentos.linhas.map((l) => (
                      <tr key={l.pagamentoId} className="border-b last:border-0">
                        <td className="px-5 py-2">
                          <Link
                            to={`/devedores/${l.devedorId}`}
                            className="text-[color:var(--acento)] hover:underline"
                          >
                            {l.devedor}
                          </Link>
                        </td>
                        <td className="px-3 py-2 text-[color:var(--texto-suave)]">
                          {l.parcelaNumero}ª
                        </td>
                        <td className="px-3 py-2">{data(l.vencimento)}</td>
                        <td className="px-3 py-2">{data(l.dataPagamento)}</td>
                        <td className="px-3 py-2">{FORMA[l.forma] ?? l.forma}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {moeda(l.valorPago)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-[color:var(--texto-suave)]">
                          {l.acrescimo > 0 ? moeda(l.acrescimo) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {moeda(l.honorarios)}
                          <span className="ml-1 text-xs text-[color:var(--texto-tenue)]">
                            ({percentual(l.percentualAplicado)})
                          </span>
                        </td>
                        <td className="px-5 py-2 text-right tabular-nums">
                          {moeda(l.liquidoClinica)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t text-sm font-medium">
                      <td className="px-5 py-3" colSpan={5}>
                        Totais
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums">
                        {moeda(relatorio.pagamentos.totais.valorPago)}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums">
                        {relatorio.pagamentos.totais.acrescimo > 0
                          ? moeda(relatorio.pagamentos.totais.acrescimo)
                          : '—'}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums">
                        {moeda(relatorio.pagamentos.totais.honorarios)}
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums">
                        {moeda(relatorio.pagamentos.totais.liquidoClinica)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Cartao>

          {/* 4. Fechamento */}
          <Cartao>
            <TituloDeSecao>Fechamento com o escritório</TituloDeSecao>
            {relatorio.fechamento.alertaPercentualZeradoComHonorario && (
              <div className="mb-4">
                <Alerta tom="atencao">
                  O percentual vigente da clínica está em 0%, mas há honorários gravados nas
                  baixas deste mês. Os valores abaixo usam o que foi congelado em cada
                  pagamento — não o percentual atual.
                </Alerta>
              </div>
            )}
            <dl className="grid gap-2 text-sm sm:grid-cols-2">
              <Linha rotulo="Base (recebido no mês)" valor={moeda(relatorio.fechamento.baseCalculo)} />
              <Linha
                rotulo={`Honorários de êxito (congelados)`}
                valor={moeda(relatorio.fechamento.honorariosExito)}
              />
              <Linha rotulo="Mensalidade" valor={moeda(relatorio.fechamento.mensalidade)} />
              <Linha
                rotulo="Reembolso de custas adiantadas"
                valor={
                  relatorio.fechamento.reembolsoCustas > 0
                    ? moeda(relatorio.fechamento.reembolsoCustas)
                    : '—'
                }
              />
              <Linha
                rotulo={`Total devido ao escritório (repasse até dia ${relatorio.fechamento.diaRepasse})`}
                valor={moeda(relatorio.fechamento.totalDevidoAoEscritorio)}
                destaque
              />
            </dl>
            {relatorio.fechamento.custas.length > 0 && (
              <ul className="mt-4 space-y-1 border-t pt-3 text-xs text-[color:var(--texto-suave)]">
                {relatorio.fechamento.custas.map((c) => (
                  <li key={c.devedorId} className="flex justify-between gap-4">
                    <Link to={`/devedores/${c.devedorId}`} className="hover:underline">
                      {c.nome}
                    </Link>
                    <span className="tabular-nums">{moeda(c.valor)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Cartao>

          {/* 5. Funil */}
          <Cartao>
            <TituloDeSecao
              auxiliar={
                relatorio.funil.variacaoDisponivel
                  ? undefined
                  : 'Variação mês a mês depende do fechamento congelado'
              }
            >
              Funil ao fim do mês
            </TituloDeSecao>
            <Funil fases={relatorio.funil.aoFimDoMes} />
          </Cartao>

          {/* 6. Movimentação */}
          <section className="grid gap-4 lg:grid-cols-2">
            <ListaMovimento
              titulo="Novos cadastros"
              vazio="Nenhum cadastro neste mês."
              itens={relatorio.movimentacao.novos.map((n) => ({
                id: n.id,
                nome: n.nome,
                detalhe: `${moeda(n.valorOriginal)} · ${data(n.cadastradoEm)}`,
              }))}
            />
            <ListaMovimento
              titulo="Acordos firmados"
              vazio="Nenhum acordo neste mês."
              itens={relatorio.movimentacao.acordosFirmados.map((a) => ({
                id: a.devedorId,
                nome: a.nome,
                detalhe: `${moeda(a.valorTotal)} em ${a.quantidadeParcelas}× · ${data(a.firmadoEm)}`,
              }))}
            />
            <ListaMovimento
              titulo="Quitados"
              vazio="Nenhuma quitação neste mês."
              itens={relatorio.movimentacao.quitados.map((q) => ({
                id: q.id,
                nome: q.nome,
                detalhe: `recuperado ${moeda(q.recuperado)} · ${data(q.em)}`,
              }))}
            />
            <ListaMovimento
              titulo="Encaminhados ao judicial"
              vazio="Nenhum encaminhamento neste mês."
              itens={relatorio.movimentacao.encaminhadosJudicial.map((e) => ({
                id: e.id,
                nome: e.nome,
                detalhe: `${moeda(e.valorOriginal)} · ${data(e.em)}`,
              }))}
            />
          </section>

          {/* 7. Negociação */}
          <Cartao semPadding>
            <div className="p-5 pb-3">
              <TituloDeSecao auxiliar={plural(relatorio.negociacao.length, 'caso', 'casos')}>
                Em negociação
              </TituloDeSecao>
            </div>
            {relatorio.negociacao.length === 0 ? (
              <p className="px-5 pb-5 text-sm text-[color:var(--texto-tenue)]">
                Nenhum caso em negociação agora.
              </p>
            ) : (
              <ul className="divide-y">
                {relatorio.negociacao.map((n) => (
                  <li key={n.id} className="flex flex-wrap items-start justify-between gap-3 px-5 py-3">
                    <div className="min-w-0">
                      <Link
                        to={`/devedores/${n.id}`}
                        className="text-sm text-[color:var(--acento)] hover:underline"
                      >
                        {n.nome}
                      </Link>
                      <p className="mt-0.5 text-xs text-[color:var(--texto-suave)]">
                        {moeda(n.valorOriginal)} · {n.diasNaFase} dia
                        {n.diasNaFase === 1 ? '' : 's'} na fase
                      </p>
                      {n.ultimaAtualizacao && (
                        <p className="mt-1 text-xs text-[color:var(--texto-tenue)]">
                          {n.ultimaAtualizacao.por} · {dataHora(n.ultimaAtualizacao.em)} —{' '}
                          {n.ultimaAtualizacao.trecho}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Cartao>

          {/* 8. Atualizações do mês */}
          <Cartao semPadding>
            <div className="p-5 pb-3">
              <TituloDeSecao
                auxiliar={plural(relatorio.atualizacoes.length, 'nota', 'notas')}
              >
                Atualizações
              </TituloDeSecao>
              <p className="text-xs text-[color:var(--texto-tenue)]">
                Notas da equipe registradas neste mês na ficha dos casos
              </p>
            </div>
            {relatorio.atualizacoes.length === 0 ? (
              <p className="px-5 pb-5 text-sm text-[color:var(--texto-tenue)]">
                Nenhuma atualização neste mês.
              </p>
            ) : (
              <ul className="divide-y">
                {relatorio.atualizacoes.map((a) => (
                  <li key={a.id} className="px-5 py-3">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <Link
                        to={`/devedores/${a.devedorId}`}
                        className="text-sm text-[color:var(--acento)] hover:underline"
                      >
                        {a.nome}
                      </Link>
                      <span className="text-xs text-[color:var(--texto-tenue)]">
                        {a.por} · {dataHora(a.em)}
                      </span>
                    </div>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-[color:var(--texto)]">
                      {a.texto}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Cartao>

          {/* 9. Promessas */}
          <Alerta tom="atencao">
            <strong>Promessas de pagamento.</strong> {relatorio.promessas.motivo}
          </Alerta>
        </div>
      )}
    </>
  );
}

function Linha({
  rotulo,
  valor,
  destaque,
}: {
  rotulo: string;
  valor: string;
  destaque?: boolean;
}) {
  return (
    <div
      className={`flex items-baseline justify-between gap-4 rounded px-3 py-2 ${
        destaque ? 'bg-[color:var(--superficie-afundada)] font-medium' : ''
      }`}
    >
      <dt className="text-[color:var(--texto-suave)]">{rotulo}</dt>
      <dd className="tabular-nums text-[color:var(--texto)]">{valor}</dd>
    </div>
  );
}

function ListaMovimento({
  titulo,
  vazio,
  itens,
}: {
  titulo: string;
  vazio: string;
  itens: Array<{ id: string; nome: string; detalhe: string }>;
}) {
  return (
    <Cartao semPadding>
      <div className="p-5 pb-3">
        <TituloDeSecao auxiliar={itens.length > 0 ? String(itens.length) : undefined}>
          {titulo}
        </TituloDeSecao>
      </div>
      {itens.length === 0 ? (
        <p className="px-5 pb-5 text-sm text-[color:var(--texto-tenue)]">{vazio}</p>
      ) : (
        <ul className="divide-y">
          {itens.map((i) => (
            <li key={`${i.id}-${i.detalhe}`} className="px-5 py-2.5">
              <Link
                to={`/devedores/${i.id}`}
                className="text-sm text-[color:var(--acento)] hover:underline"
              >
                {i.nome}
              </Link>
              <p className="text-xs text-[color:var(--texto-suave)]">{i.detalhe}</p>
            </li>
          ))}
        </ul>
      )}
    </Cartao>
  );
}
